import type { EvidenceSummary } from '@/api/types/compliance-dashboard.types';

// Slice 2 (BI): evidence records + expiry. Evidence is metadata + approved-location references,
// NOT file uploads — "without" counts implemented controls with no evidence record, never a
// failed check. All figures are real; zeros grey out.
interface EvidenceHealthPanelProps {
  evidence: EvidenceSummary;
  isLoading: boolean;
}

function EvTile({
  value,
  label,
  color,
  borderColor,
}: {
  value: number;
  label: string;
  color: string;
  borderColor?: string;
}) {
  return (
    <div
      className="flex flex-col gap-[3px] rounded-[9px] border border-border-subtle bg-[#0D1626] p-[10px_12px]"
      style={borderColor ? { borderColor } : undefined}
      data-testid="evidence-tile"
    >
      <span className="text-[20px] font-bold leading-[1.1] tabular-nums" style={{ color }}>
        {value}
      </span>
      <span className="text-[11px] leading-[1.35] text-text-muted">{label}</span>
    </div>
  );
}

export function EvidenceHealthPanel({ evidence, isLoading }: EvidenceHealthPanelProps) {
  return (
    <div
      className="flex min-w-0 flex-col gap-[11px] rounded-xl border border-border-subtle bg-bg-surface p-[14px_16px_13px]"
      data-testid="evidence-health"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[13px] font-semibold text-text-primary">Evidence health</span>
        <span className="ml-auto text-[10.5px] text-[#7A8CA3]">Records &amp; expiry</span>
      </div>
      {isLoading ? (
        <p className="text-[11.5px] text-text-secondary">Loading…</p>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(118px,1fr))] gap-[9px]">
          <EvTile value={evidence.records} label="Evidence records" color={evidence.records > 0 ? '#F8FAFC' : '#5C7189'} />
          <EvTile
            value={evidence.expired}
            label="Expired"
            color={evidence.expired > 0 ? '#F09E99' : '#5C7189'}
            borderColor={evidence.expired > 0 ? '#6E3A20' : undefined}
          />
          <EvTile
            value={evidence.expiring}
            label="Expiring in 30 days"
            color={evidence.expiring > 0 ? '#F0C674' : '#5C7189'}
            borderColor={evidence.expiring > 0 ? '#5E4A1E' : undefined}
          />
          <EvTile
            value={evidence.without}
            label="Controls without evidence records"
            color={evidence.without > 0 ? '#8298B0' : '#5C7189'}
          />
        </div>
      )}
      <span className="mt-auto border-t border-[#1B2C43] pt-[9px] text-[11.5px] leading-[1.55] text-text-secondary">
        Evidence records store metadata and approved evidence locations. Actual evidence remains in the approved
        repository.
      </span>
    </div>
  );
}
